import React from 'react';
import ReactLoading from 'react-loading';
import moment from 'moment-jalaali';
import { toPersianDigits, formatPrice, getStatusTitleById } from '../utils';

export const InvoiceModal = ({ order, onClose, isLoading }) => {
    if (!order) return null;
    
    const invoiceData = order.invoice || {};
    const items = invoiceData.items || [];
    
    // هوشمندسازی دریافت مقادیر: هم با ساختار summary و هم با ساختار totals کار می‌کند
    const rawSummary = invoiceData.summary || {};
    const rawTotals = invoiceData.totals || {};
    
    // پاک‌سازی مبلغ کل (چون بک‌اند قیمت را با کاما به صورت "4,332,523" می‌فرستد)
    const rawPrice = order?.price 
        ? parseInt(String(order.price).replace(/,/g, '')) 
        : 0;
    
    const summary = {
        totalConsumer: rawSummary.totalConsumer ?? rawTotals.consumer ?? 0,
        totalProfit: rawSummary.totalProfit ?? rawTotals.profit ?? 0,
        totalVat: rawSummary.totalVat ?? rawTotals.vat ?? 0,
        totalShippingAndPack: rawSummary.totalShippingAndPack ?? rawTotals.shipping ?? 0,
        finalPayment: rawSummary.finalPayment ?? rawPrice ?? 0
    };

    const seller = invoiceData.seller || { name: order.shop };
    const buyer = invoiceData.buyer || { name: 'در حال بارگذاری...' };

    const getStatusWatermark = () => {
        if (order.status === 'cancelled') return { text: 'ابطال شده', className: 'wm-cancelled' };
        else if (order.currentStep === 6) return { text: 'تحویل شده', className: 'wm-completed' };
        else return { text: getStatusTitleById(order.currentStep), className: 'wm-processing' };
    };

    const watermarkInfo = getStatusWatermark();

    const handleDownloadPDF = () => {
        if (isLoading) return;
        try {
            const dataToSend = { ...invoiceData, id: order.id, factorNumber: order.factorNumber, date: order.date, status: order.status, currentStep: order.currentStep, trackingCodePost: order.trackingCodePost };
            const jsonString = JSON.stringify(dataToSend);
            const utf8Bytes = new TextEncoder().encode(jsonString);
            const base64Data = btoa(String.fromCharCode(...utf8Bytes));
            const printUrl = `/print?type=invoice&autoDownload=true&data=${encodeURIComponent(base64Data)}`;
            window.open(printUrl, '_blank');
        } catch (error) {
            console.error("Error:", error);
            alert("مشکل در ایجاد فایل PDF");
        }
    };

    // تابع بهینه‌شده برای نمایش دقیق تاریخ و ساعت به شمسی
    const formatFullShamsiDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;

        return new Intl.DateTimeFormat('fa-IR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        }).format(date);
    };

    return (
        <div className="modal-backdrop" onClick={onClose} style={{ zIndex: 1100 }}>
            <button className="close-btn-fixed" onClick={onClose}>✕</button>
            <div className="modal-scroll-container" onClick={e => e.stopPropagation()}>
                <div id="receipt-content" className="receipt-paper" style={{ minHeight: '400px', display: 'flex', flexDirection: 'column' }}>
                    <div className={`watermark ${watermarkInfo.className}`}>{watermarkInfo.text}</div>

                    {isLoading ? (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <ReactLoading type="spinningBubbles" color="#3b82f6" height={50} width={50} />
                            <span style={{ marginTop: '20px', color: '#64748b', fontSize: '14px', fontWeight: 'bold' }}>در حال دریافت جزئیات فاکتور...</span>
                        </div>
                    ) : (
                        <div className="receipt-content-layer">
                            <div className="receipt-header">
                                <h2 className="shop-name">{"صورتحساب کالا و خدمات"}</h2>
                                <div
                                    className="receipt-meta"
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '10px',
                                        width: '100%'
                                    }}
                                >
                                    <div style={{ fontWeight: 'bold' }}>
                                        شماره: {toPersianDigits(order.factorNumber)}
                                    </div>
                                    <div>
                                        تاریخ: {toPersianDigits(moment(order.date).format('jYYYY/jMM/jDD'))}
                                        <span style={{ margin: '0 5px' }}>|</span>
                                        ساعت: {toPersianDigits(moment(order.date).format('HH:mm'))}
                                    </div>
                                </div>
                            </div>
                            <div className="dashed-line"></div>
                            <div className="receipt-section">
                                <div className="info-row"><div>فروشنده: <span className="bold">{seller?.name}</span></div></div>
                                <div className="info-row">
                                    <div>خریدار: <span className="bold">{buyer?.name}</span></div>
                                </div>

                                {(buyer?.address || order.address_buyer_archive) && (
                                    <div className="info-row" style={{ marginTop: '4px', fontSize: '11px', lineHeight: '1.8' }}>
                                        <div>آدرس: <span className="bold">{buyer?.address || order.address_buyer_archive}</span></div>
                                    </div>
                                )}

                                {(buyer?.postCode || order.post_code_buyer_archive) && (
                                    <div className="info-row" style={{ marginTop: '4px', fontSize: '11px' }}>
                                        <div>کد پستی: <span className="bold">{buyer?.postCode || order.post_code_buyer_archive}</span></div>
                                    </div>
                                )}

                                {order.trackingCodePost && (
                                    <div className="info-row" style={{ marginTop: '0px', fontSize: '12px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            کد رهگیری پستی:
                                            <span className="bold" style={{ backgroundColor: '#f1f5f9', padding: '3px 5px', borderRadius: '4px', letterSpacing: '2px', border: '1px solid #cbd5e1' }} dir="ltr">
                                                {order.trackingCodePost}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="dashed-line"></div>

                            {items.length > 0 ? (
                                <div className="receipt-table" style={{ fontSize: '13px' }}>
                                    <div className="table-header" style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 2fr 2fr 2fr', gap: '5px', textAlign: 'center', fontWeight: 'bold', paddingBottom: '10px', borderBottom: '2px solid #000' }}>
                                        <div style={{ textAlign: 'right' }}>شرح کالا</div>
                                        <div>تعداد</div>
                                        <div>قیمت اصلی</div>
                                        <div>قیمت با تخفیف</div>
                                        <div>مبلغ کل</div>
                                    </div>
                                    {items.map((item, index) => (
                                        <div key={index} className="table-row" style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 2fr 2fr 2fr', gap: '5px', textAlign: 'center', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                                            <div style={{ textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{toPersianDigits(item.name)}</div>
                                            <div>{toPersianDigits(item.count)}</div>
                                            <div>{toPersianDigits(formatPrice(item.originalPrice))}</div>
                                            <div>{toPersianDigits(formatPrice(item.ourPrice))}</div>
                                            <div>{toPersianDigits(formatPrice(item.total))}</div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8', fontSize: '13px' }}>اقلام فاکتور یافت نشد</div>
                            )}

                            <div className="dashed-line"></div>

                            <div className="receipt-summary" style={{ display: 'flex', flexDirection: 'column', gap: '15px', fontSize: '14px' }}>
                                <div className="summary-row" style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>مجموع قیمت مصرف کننده:</span>
                                    <span>{toPersianDigits(formatPrice(summary.totalConsumer))}</span>
                                </div>
                                <div className="summary-row" style={{ display: 'flex', justifyContent: 'space-between', backgroundColor: '#e2e8f0', padding: '10px', borderRadius: '4px', fontWeight: 'bold' }}>
                                    <span>سود شما از این خرید:</span>
                                    <span>{toPersianDigits(formatPrice(summary.totalProfit))}</span>
                                </div>
                                <div className="summary-row" style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>ارزش افزوده:</span>
                                    <span>{toPersianDigits(formatPrice(summary.totalVat))}</span>
                                </div>
                                <div className="summary-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                        <span>هزینه بسته بندی و ارسال:</span>
                                        {summary.totalShippingAndPack === 0 && (
                                            <span style={{ color: '#ef4444', fontSize: '12px' }}>(هزینه ارسال: پس‌کرایه - پرداخت در محل)</span>
                                        )}
                                    </div>
                                    <span>{toPersianDigits(formatPrice(summary.totalShippingAndPack))}</span>
                                </div>
                                <div className="summary-row total-pay" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: 'bold', marginTop: '10px' }}>
                                    <span>مبلغ قابل پرداخت (تومان):</span>
                                    <span>{toPersianDigits(formatPrice(summary.finalPayment))}</span>
                                </div>
                            </div>

                            {/* --- بخش اطلاعات تراکنش‌های مالی --- */}
                            {order.finance_trnsactions && order.finance_trnsactions.length > 0 && (
                                <>
                                    <div className="dashed-line" style={{ margin: '15px 0' }}></div>
                                    <div className="receipt-transactions" style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '12px' }}>
                                        <div style={{ fontWeight: 'bold', fontSize: '14px' }}>اطلاعات پرداخت</div>
                                        {order.finance_trnsactions.map((tx, index) => {
                                            const isCardToCard = String(tx.transactionType_id) === "1";
                                            
                                            return (
                                                <div key={tx.id || index} style={{ backgroundColor: '#f8fafc', padding: '12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                                        <div>
                                                            <span style={{ color: '#64748b' }}>مبلغ پرداختی: </span>
                                                            <span style={{ fontWeight: 'bold', color: '#16a34a' }}>{toPersianDigits(formatPrice(tx.totalPrice))} تومان</span>
                                                        </div>
                                                        <div>
                                                            <span style={{ color: '#64748b' }}>تاریخ و ساعت: </span>
                                                            <span>{toPersianDigits(formatFullShamsiDate(tx.date))}</span>
                                                        </div>
                                                        
                                                        {isCardToCard ? (
                                                            <>
                                                                <div>
                                                                    <span style={{ color: '#64748b' }}>کارت مبدا: </span>
                                                                    <span dir="ltr" style={{ display: 'inline-block' }}>{tx.sourceCardNumber ? toPersianDigits(tx.sourceCardNumber) : '-'}</span>
                                                                </div>
                                                                <div>
                                                                    <span style={{ color: '#64748b' }}>کارت مقصد: </span>
                                                                    <span dir="ltr" style={{ display: 'inline-block' }}>{tx.destinationCardNumber ? toPersianDigits(tx.destinationCardNumber) : '-'}</span>
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <div>
                                                                    <span style={{ color: '#64748b' }}>روش پرداخت: </span>
                                                                    <span className="bold">{tx.gatewayName ? tx.gatewayName : 'درگاه اینترنتی'}</span>
                                                                </div>
                                                                {tx.referenceId && (
                                                                    <div>
                                                                        <span style={{ color: '#64748b' }}>شماره مرجع بانکی: </span>
                                                                        <span dir="ltr" style={{ display: 'inline-block' }}>{toPersianDigits(tx.referenceId)}</span>
                                                                    </div>
                                                                )}
                                                                {tx.cardNumber && (
                                                                    <div>
                                                                        <span style={{ color: '#64748b' }}>شماره کارت (پرداخت‌کننده): </span>
                                                                        <span dir="ltr" style={{ display: 'inline-block' }}>{toPersianDigits(tx.cardNumber)}</span>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                            {/* --- پایان بخش اطلاعات تراکنش‌های مالی --- */}

                        </div>
                    )}
                </div>

                <div className="modal-actions-container">
                    <button
                        className="print-btn"
                        onClick={handleDownloadPDF}
                        disabled={isLoading}
                        style={{ backgroundColor: isLoading ? '#94a3b8' : '#3b82f6', cursor: isLoading ? 'not-allowed' : 'pointer', width: '100%', padding: '12px', border: 'none', borderRadius: '8px', color: 'white', fontWeight: 'bold', fontSize: '15px' }}
                    >
                        {isLoading ? 'لطفا صبر کنید...' : 'دانلود فایل PDF'}
                    </button>
                </div>
            </div>
        </div>
    );
};
